import React, { useEffect } from 'react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { closeModal, closeSecondaryModal, openModal, openSecondaryModal, setHeadbarTitle } from 'helpers/actions'
import { useAppDispatch } from 'helpers/store'
import LeadBasicForm from 'components/leads/forms/LeadBasicForm'
import EditLeadModal from 'components/leads/EditLeadModal'
import { Lead } from 'types/entities/Lead'
import { Company } from 'types/entities/Company'
import { CompanyContact } from 'types/entities/CompanyContact'
import { ModalSize } from 'types/enums/ModalSize'
import { LeadStatus } from 'types/enums/LeadStatus'
import { LeadCategory } from 'types/enums/LeadCategory'
import LeadsAction from 'store/leads/LeadsAction'
import UiFormModalShell from 'components/Forms/UiFormModalShell'
import { toast } from 'utils/toastUtils'
import { capturePosthogEvent } from 'utils/posthogUtils'

interface CreateLeadModalProps {
    onSuccess?: (lead: Lead) => void
    company?: Company | null
    predefinedContact?: CompanyContact | null
    isSecondary?: boolean
    initialLead?: Partial<Lead>
    disableCompanyChange?: boolean
}

const CreateLeadModal: React.FC<CreateLeadModalProps> = ({
    onSuccess,
    company,
    predefinedContact,
    isSecondary,
    initialLead,
    disableCompanyChange = false,
}) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const isOpportunity = initialLead?.category === LeadCategory.Opportunity
    const createTitle = t(isOpportunity ? 'Create Opportunity' : 'Create Lead')

    const handleSuccess = (lead: Lead, shouldClose: boolean = false) => {
        capturePosthogEvent('lead_created', {
            lead_id: lead.id,
            lead_status: lead.status,
            lead_category: lead.category,
            has_company: !!lead.company_id,
        })
        if (onSuccess) {
            onSuccess(lead)
        }
        if (shouldClose) {
            if (isSecondary) {
                dispatch(closeSecondaryModal())
            } else {
                dispatch(closeModal())
            }
            dispatch(setHeadbarTitle(null))
            return
        }
        if (isSecondary) {
            dispatch(closeSecondaryModal())
        } else {
            dispatch(closeModal())
        }
        dispatch(setHeadbarTitle(null))
        const openModalAction = isSecondary ? openSecondaryModal : openModal
        const closeModalAction = isSecondary ? closeSecondaryModal : closeModal

        dispatch(
            openModalAction(
                <EditLeadModal lead={lead} onSuccess={() => {}} isSecondary={isSecondary} />,
                {
                    modalSize: ModalSize.RoundedFullscreen,
                    closeModal: () => {
                        dispatch(LeadsAction.resetSelectedLead())
                        dispatch(closeModalAction())
                    },
                    onError: () => {
                        toast.error(t('An error occurred'))
                    },
                },
                t('Edit Lead')
            )
        )
    }

    useEffect(() => {
        dispatch(LeadsAction.resetSelectedLead())
        dispatch(setHeadbarTitle(createTitle))
        return () => {
            dispatch(setHeadbarTitle(null))
        }
    }, [createTitle, dispatch])

    return (
        <UiFormModalShell
            className={clsx('CreateLeadModal')}
            title={createTitle}
            subtitle={t('Fill in the lead details below')}
            bodyClassName="flex flex-col px-6 py-6"
        >
            <div className={clsx('space-y-6')}>
                <LeadBasicForm
                    onSuccess={handleSuccess}
                    company={company}
                    predefinedContact={predefinedContact}
                    disableCompanyChange={disableCompanyChange}
                    lead={{ status: LeadStatus.New, ...initialLead } as Lead}
                />
            </div>
        </UiFormModalShell>
    )
}

export default CreateLeadModal
